import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  RadialBarChart, RadialBar, Sankey
} from 'recharts';
import { 
  Users, TrendingUp, Target, Activity, Clock, MousePointer,
  ChevronRight, Eye, Brain, Sparkles, UserCheck, Globe,
  ChevronLeft, MessageCircle
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { formatDistanceToNow } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import { useSite } from '@/contexts/SiteContext';
import { authFetch } from '@/utils/auth-fetch';

// Maturity level configuration
const maturityConfig = {
  explorer: { color: '#9CA3AF', icon: '🔍', label: 'Explorer' },
  interested: { color: '#60A5FA', icon: '👀', label: 'Interested' },
  engaged: { color: '#F59E0B', icon: '⚡', label: 'Engaged' },
  qualified: { color: '#10B981', icon: '🎯', label: 'Qualified' }
};

export default function BehaviorDashboard() {
  const { selectedSite } = useSite();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('90'); // Default to last 90 days
  const [behaviorData, setBehaviorData] = useState<any>(null);
  const [selectedSegment, setSelectedSegment] = useState('all');
  const navigate = useNavigate();
  
  // AI Insights state
  const [behaviorInsights, setBehaviorInsights] = useState<Array<{title: string, content: string, type: string}>>([]);
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    console.log('[BehaviorDashboard] Effect triggered - selectedSite:', selectedSite, 'dateRange:', dateRange);
    if (selectedSite) {
      fetchBehaviorData();
    }
  }, [selectedSite, dateRange]);

  // Track page visit for onboarding
  useEffect(() => {
    if (selectedSite?._id) {
      authFetch('/onboarding/track', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId: selectedSite._id,
          activity: {
            type: 'behavior_visited',
            path: '/behavior'
          }
        })
      }).catch(err => console.error('Failed to track behavior visit:', err));
    }
  }, [selectedSite]);

  // Site selection is now handled by global context

  const fetchBehaviorData = async () => {
    if (!selectedSite) return;
    
    const siteId = selectedSite._id || selectedSite.id;
    console.log('[BehaviorDashboard] Fetching behavior data for site:', siteId);
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/analytics/behavior/${siteId}?days=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        },
      });
      console.log('[BehaviorDashboard] Behavior response:', response.status);
      if (!response.ok) throw new Error(`Failed to fetch behavior data: ${response.status}`);
      const data = await response.json();
      console.log('[BehaviorDashboard] Behavior data:', data);
      setBehaviorData(data);
      
      // Generate AI insights
      generateBehaviorInsights(data);
    } catch (error) {
      console.error('[BehaviorDashboard] Failed to fetch behavior data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const generateBehaviorInsights = async (data: any) => {
    setInsightsLoading(true);
    setBehaviorInsights([]);
    setCurrentInsightIndex(0);
    
    try {
      const context = {
        siteName: selectedSite?.name || selectedSite?.url,
        currentDistribution: data.currentDistribution,
        progressionInsights: data.progressionInsights,
        topMicroConversions: data.topMicroConversions,
        summary: data.summary,
        dateRange
      };
      
      const response = await authFetch('/agent-insights/behavior-analyst', {
        method: 'POST',
        body: JSON.stringify({
          context,
          prompt: `Analyze this visitor behavior data and provide insights about:
1. Visitor maturity patterns and distribution
2. Key progression drivers and blockers
3. Most effective micro conversions
4. Behavioral segments and characteristics  
5. Optimization opportunities
6. Specific recommendations for improving visitor qualification`
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.insights && Array.isArray(result.insights)) {
          setBehaviorInsights(result.insights);
        }
      }
    } catch (error) {
      console.error('Failed to generate behavior insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  if (loading || !behaviorData) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const { currentDistribution, historicalTrends, topMicroConversions, progressionInsights, summary } = behaviorData;

  // Format data for funnel chart
  const funnelData = Object.entries(currentDistribution).map(([level, data]: [string, any]) => ({
    name: data.name,
    value: data.count,
    percentage: data.percentage.toFixed(1),
    fill: data.color,
    icon: data.icon
  }));

  // Format data for progression flow
  const progressionFlowData = progressionInsights.slice(0, 5).map((insight: any) => ({
    source: insight.from,
    target: insight.to,
    value: insight.count,
    time: `~${Math.round(insight.avgTimeHours)}h`
  }));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8 text-purple-600" />
            Visitor Behavior Analytics
          </h1>
          <p className="text-gray-600 mt-1">
            Track visitor maturity and conversion likelihood based on behavioral patterns
          </p>
        </div>
        <div className="flex gap-3">
          {/* Site selection is now handled by global context */}
          <Card className="w-64">
            <CardHeader className="py-2 px-4">
              <CardDescription className="text-xs">Current Site</CardDescription>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {selectedSite?.name || selectedSite?.url || 'No site selected'}
              </CardTitle>
            </CardHeader>
          </Card>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Visitors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary.totalVisitors.toLocaleString()}</div>
            <p className="text-xs text-gray-600 mt-1">Tracked in period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Qualified Visitors</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summary.qualifiedVisitors.toLocaleString()}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {summary.qualificationRate.toFixed(1)}% qualification rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Avg Progression</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-1">
              {summary.avgProgressionRate.toFixed(1)}%
              <TrendingUp className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-xs text-gray-600 mt-1">Between levels</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Health Score</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <Progress value={summary.healthScore} className="flex-1" />
              <span className="text-sm font-bold">{summary.healthScore}%</span>
            </div>
            <p className="text-xs text-gray-600 mt-1">Overall performance</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="progression">Progression</TabsTrigger>
          <TabsTrigger value="conversions">Micro Conversions</TabsTrigger>
          <TabsTrigger value="segments">Segments</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Maturity Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Visitor Maturity Distribution</CardTitle>
                <CardDescription>Current visitor breakdown by engagement level</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(currentDistribution).map(([level, data]: [string, any]) => (
                    <div key={level} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">{data.icon}</span>
                          <span className="font-medium">{data.name}</span>
                          <Badge variant="secondary">{data.count}</Badge>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">{data.percentage.toFixed(1)}%</div>
                          <div className="text-xs text-gray-600">
                            ~{data.avgLikelihood}% likely
                          </div>
                        </div>
                      </div>
                      <Progress 
                        value={data.percentage} 
                        className="h-2"
                        style={{ '--progress-color': data.color } as any}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Conversion Funnel */}
            <Card>
              <CardHeader>
                <CardTitle>Conversion Funnel</CardTitle>
                <CardDescription>Visitor progression through maturity levels</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={funnelData} layout="horizontal">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload[0]) {
                          const data = payload[0].payload;
                          return (
                            <div className="bg-white p-2 border rounded shadow">
                              <p className="font-medium">{data.icon} {data.name}</p>
                              <p className="text-sm">{data.value} visitors ({data.percentage}%)</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="value" fill="#8884d8">
                      {funnelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Historical Trends */}
          <Card>
            <CardHeader>
              <CardTitle>Maturity Trends</CardTitle>
              <CardDescription>Visitor distribution over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={historicalTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="explorer" stackId="1" stroke="#9CA3AF" fill="#9CA3AF" name="Explorer" />
                  <Area type="monotone" dataKey="interested" stackId="1" stroke="#60A5FA" fill="#60A5FA" name="Interested" />
                  <Area type="monotone" dataKey="engaged" stackId="1" stroke="#F59E0B" fill="#F59E0B" name="Engaged" />
                  <Area type="monotone" dataKey="qualified" stackId="1" stroke="#10B981" fill="#10B981" name="Qualified" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progression Tab */}
        <TabsContent value="progression" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Visitor Progression Flow</CardTitle>
              <CardDescription>How visitors move between maturity levels</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {progressionFlowData.map((flow: any, index: number) => (
                  <div key={index} className="flex items-center gap-4 p-4 border rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Badge style={{ backgroundColor: maturityConfig[flow.source.toLowerCase()]?.color }}>
                          {flow.source}
                        </Badge>
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                        <Badge style={{ backgroundColor: maturityConfig[flow.target.toLowerCase()]?.color }}>
                          {flow.target}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold">{flow.value}</div>
                      <div className="text-sm text-gray-600">visitors</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">{flow.time}</div>
                      <div className="text-xs text-gray-600">avg time</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Progression Rates Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Progression Rates Over Time</CardTitle>
              <CardDescription>Percentage of visitors progressing between levels</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={historicalTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis tickFormatter={(value) => `${value}%`} />
                  <Tooltip formatter={(value: any) => `${value.toFixed(1)}%`} />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="progressionRates.explorerToInterested" 
                    stroke="#60A5FA" 
                    name="Explorer → Interested"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="progressionRates.interestedToEngaged" 
                    stroke="#F59E0B" 
                    name="Interested → Engaged"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="progressionRates.engagedToQualified" 
                    stroke="#10B981" 
                    name="Engaged → Qualified"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Micro Conversions Tab */}
        <TabsContent value="conversions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Top Micro Conversions</CardTitle>
              <CardDescription>Key actions driving visitor maturity</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topMicroConversions.map((conversion: any, index: number) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Sparkles className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <div className="font-medium">{conversion.name}</div>
                        <div className="text-sm text-gray-600">
                          Stage: <Badge variant="outline" className="ml-1">{conversion.stage}</Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold">{conversion.visitor_count}</div>
                      <div className="text-sm text-gray-600">visitors</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-purple-600">+{conversion.points} pts</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Conversion Impact */}
          <Card>
            <CardHeader>
              <CardTitle>Conversion Impact Analysis</CardTitle>
              <CardDescription>How micro conversions correlate with qualification</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topMicroConversions}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="visitor_count" fill="#8B5CF6" name="Visitors" />
                  <Bar dataKey="points" fill="#F59E0B" name="Impact Points" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Segments Tab */}
        <TabsContent value="segments" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Behavioral Segments</CardTitle>
              <CardDescription>Visitor groups based on behavior patterns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3">High-Intent Visitors</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Viewed pricing</span>
                      <Badge>{currentDistribution.qualified.count * 0.7 | 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Requested demo</span>
                      <Badge>{currentDistribution.qualified.count * 0.3 | 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Multi-session</span>
                      <Badge>{currentDistribution.engaged.count * 0.6 | 0}</Badge>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <h4 className="font-semibold mb-3">Content Consumers</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Blog readers</span>
                      <Badge>{currentDistribution.interested.count * 0.5 | 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Resource downloads</span>
                      <Badge>{currentDistribution.interested.count * 0.2 | 0}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Video watchers</span>
                      <Badge>{currentDistribution.engaged.count * 0.3 | 0}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* AI-Powered Behavioral Insights */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">AI-Powered Behavioral Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-4">
                {imageError ? (
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200 flex-shrink-0">
                    <span className="text-purple-600 text-sm font-bold">BA</span>
                  </div>
                ) : (
                  <img 
                    src="/crew-agents/behavior-analyst.jpg" 
                    alt="Behavior Analyst"
                    className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                    onError={() => setImageError(true)}
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">Behavior Analyst</p>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center gap-1"
                      onClick={() => {
                        navigate('/insights/hub/behavior-analyst');
                      }}
                    >
                      <MessageCircle className="w-3 h-3" />
                      Let's talk
                    </Button>
                  </div>
                  {insightsLoading ? (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-2 flex items-center">
                        <span className="font-medium">Behavior Analyst</span>
                        <span className="ml-2">analyzing patterns</span>
                        <span className="ml-1 inline-flex">
                          <span className="animate-bounce animation-delay-0">.</span>
                          <span className="animate-bounce animation-delay-100">.</span>
                          <span className="animate-bounce animation-delay-200">.</span>
                        </span>
                      </p>
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-full" />
                      <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6" />
                    </div>
                  ) : behaviorInsights.length > 1 ? (
                    <div className="space-y-3">
                      <div className="relative">
                        <div className="flex items-start gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                            disabled={currentInsightIndex === 0}
                            onClick={() => setCurrentInsightIndex(prev => Math.max(0, prev - 1))}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          
                          <div className="flex-1 min-h-[60px]">
                            {behaviorInsights[currentInsightIndex] && (
                              <>
                                <h4 className="text-sm font-medium text-gray-700 mb-1">
                                  {behaviorInsights[currentInsightIndex].title}
                                </h4>
                                <div 
                                  className="text-sm text-gray-600 leading-relaxed"
                                  dangerouslySetInnerHTML={{ __html: behaviorInsights[currentInsightIndex].content }}
                                />
                              </>
                            )}
                          </div>
                          
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 flex-shrink-0 hover:bg-gray-100"
                            disabled={currentInsightIndex === behaviorInsights.length - 1}
                            onClick={() => setCurrentInsightIndex(prev => Math.min(behaviorInsights.length - 1, prev + 1))}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-center mt-3 gap-1">
                          {behaviorInsights.map((_, index) => (
                            <button
                              key={index}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                index === currentInsightIndex ? 'bg-primary' : 'bg-gray-300'
                              }`}
                              onClick={() => setCurrentInsightIndex(index)}
                            />
                          ))}
                        </div>
                        
                        <p className="text-xs text-center text-gray-500 mt-2">
                          {currentInsightIndex + 1} of {behaviorInsights.length} insights
                        </p>
                      </div>
                      
                      <p className="text-xs text-gray-500 italic text-right">
                        — Behavior Analyst
                      </p>
                    </div>
                  ) : behaviorInsights.length === 1 ? (
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-1">
                          {behaviorInsights[0].title}
                        </h4>
                        <div 
                          className="text-sm text-gray-600 leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: behaviorInsights[0].content }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 italic text-right">
                        — Behavior Analyst
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Eye className="h-5 w-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-blue-900">Peak Engagement Window</p>
                            <p className="text-sm text-blue-700 mt-1">
                              Visitors who spend 5-10 minutes on their first visit are 3x more likely to become qualified leads.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Target className="h-5 w-5 text-green-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-green-900">Conversion Path</p>
                            <p className="text-sm text-green-700 mt-1">
                              The most common path to qualification: Home → Features → Pricing → Demo Request (42% of qualified visitors).
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="p-3 bg-amber-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Activity className="h-5 w-5 text-amber-600 mt-0.5" />
                          <div>
                            <p className="font-medium text-amber-900">Engagement Trigger</p>
                            <p className="text-sm text-amber-700 mt-1">
                              Interactive demos and product videos increase progression rate by 65% compared to text-only content.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Integration Status */}
      <Card>
        <CardHeader>
          <CardTitle>Analytics Integration Status</CardTitle>
          <CardDescription>Connected analytics platforms providing behavioral data</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                📊
              </div>
              <div>
                <div className="font-medium">Web Analytics</div>
                <div className="text-sm text-gray-600">Goals & conversions</div>
              </div>
              <Badge variant="success" className="ml-auto">Active</Badge>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                🎬
              </div>
              <div>
                <div className="font-medium">Session Replay</div>
                <div className="text-sm text-gray-600">Session quality</div>
              </div>
              <Badge variant="success" className="ml-auto">Active</Badge>
            </div>

            <div className="flex items-center gap-3 p-3 border rounded-lg">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                🧠
              </div>
              <div>
                <div className="font-medium">Behavioral Sensing</div>
                <div className="text-sm text-gray-600">Event streams</div>
              </div>
              <Badge variant="success" className="ml-auto">Active</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}